import type { StateChangeListener, TaskState } from "./TaskProvider.types"

export abstract class TaskAbstract<P = unknown, R = unknown> {
  private state: TaskState<P, R> = { status: "idle", payload: undefined }
  private listeners = new Set<StateChangeListener<P, R>>()

  constructor(
    protected payload: P,
    private settings: { globalAbortSignal?: AbortSignal }
  ) {}

  private get isAborted() {
    return this.settings.globalAbortSignal?.aborted
  }

  private setState(state: TaskState<P, R>) {
    if (this.isAborted) return

    this.state = state
    this.emit(this.state)
  }

  protected emit(state: TaskState<P, R>) {
    if (this.isAborted) return

    this.listeners.forEach((listener) => listener(state))
  }

  public onStateChange(listener: StateChangeListener<P, R>): () => void {
    this.listeners.add(listener)

    return () => {
      this.listeners.delete(listener)
    }
  }

  public async start() {
    const payload = this.payload

    try {
      this.setState({ status: "running", payload })

      const { ok, result, error } = await this.execute()

      if (ok) this.setState({ status: "success", payload, result })
      else this.setState({ status: "error", result: null, payload, error })
    } catch (error) {
      this.setState({ status: "error", result: null, payload, error })
    }
  }

  protected abstract execute(): Promise<{
    ok: boolean
    result: R | null
    error?: Error | unknown
  }>
}
